import { useState } from 'react';
import { supabase } from '../supabaseClient';
import { ShieldCheck, KeyRound, UserPlus, Eye, EyeOff, CheckCircle2, AlertCircle } from 'lucide-react';

// ─── Helper: inline alert component ────────────────────────────────────────────
function Alert({ type, message }) {
    if (!message) return null;
    const isSuccess = type === 'success';
    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            background: isSuccess ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
            color: isSuccess ? 'var(--accent-success)' : 'var(--accent-danger)',
            border: `1px solid ${isSuccess ? 'rgba(16,185,129,0.25)' : 'rgba(239,68,68,0.25)'}`,
            padding: '12px 16px', borderRadius: '8px', fontSize: '0.875rem', marginTop: '16px'
        }}>
            {isSuccess ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
            {message}
        </div>
    );
}

// ─── Password Input with show/hide toggle ───────────────────────────────────────
function PasswordInput({ label, value, onChange, show, onToggle, placeholder }) {
    return (
        <div className="form-group">
            <label className="form-label">{label}</label>
            <div style={{ position: 'relative' }}>
                <input
                    type={show ? 'text' : 'password'}
                    className="form-input"
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                    required
                    style={{ paddingRight: '44px' }}
                />
                <button
                    type="button"
                    onClick={onToggle}
                    style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center' }}
                >
                    {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
            </div>
        </div>
    );
}

// ─── Section A: Change Password ─────────────────────────────────────────────────
function ChangePasswordSection() {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState({ type: null, message: null });

    const passwordStrength = (pwd) => {
        let score = 0;
        if (pwd.length >= 8) score++;
        if (/[A-Z]/.test(pwd)) score++;
        if (/[0-9]/.test(pwd)) score++;
        if (/[^A-Za-z0-9]/.test(pwd)) score++;
        return score;
    };

    const strengthLabel = ['', 'Weak', 'Fair', 'Good', 'Strong'];
    const strengthColor = ['', '#EF4444', '#F59E0B', '#3B82F6', '#10B981'];
    const strength = passwordStrength(newPassword);

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setStatus({ type: null, message: null });

        if (newPassword !== confirmPassword) {
            setStatus({ type: 'error', message: 'New passwords do not match.' });
            return;
        }
        if (newPassword.length < 8) {
            setStatus({ type: 'error', message: 'Password must be at least 8 characters.' });
            return;
        }

        setLoading(true);
        try {
            // Re-authenticate first with the current password to verify identity
            const { data: { user } } = await supabase.auth.getUser();
            const { error: signInError } = await supabase.auth.signInWithPassword({
                email: user.email,
                password: currentPassword,
            });

            if (signInError) {
                setStatus({ type: 'error', message: 'Current password is incorrect.' });
                setLoading(false);
                return;
            }

            // Now update to new password
            const { error } = await supabase.auth.updateUser({ password: newPassword });
            if (error) throw error;

            setStatus({ type: 'success', message: 'Password updated successfully!' });
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (err) {
            setStatus({ type: 'error', message: err.message });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                <div style={{ background: 'rgba(59,130,246,0.1)', padding: '10px', borderRadius: '10px' }}>
                    <KeyRound size={20} color="var(--accent-primary)" />
                </div>
                <div>
                    <h2 style={{ fontSize: '1.125rem', fontWeight: 600, margin: 0 }}>Change Password</h2>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: '4px 0 0' }}>Update your admin account password</p>
                </div>
            </div>

            <form onSubmit={handleChangePassword} style={{ maxWidth: '480px' }}>
                <PasswordInput
                    label="Current Password"
                    value={currentPassword}
                    onChange={e => setCurrentPassword(e.target.value)}
                    show={showCurrent}
                    onToggle={() => setShowCurrent(p => !p)}
                    placeholder="Enter current password"
                />
                <PasswordInput
                    label="New Password"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    show={showNew}
                    onToggle={() => setShowNew(p => !p)}
                    placeholder="Min. 8 characters"
                />

                {/* Strength meter */}
                {newPassword && (
                    <div style={{ marginTop: '-12px', marginBottom: '20px' }}>
                        <div style={{ display: 'flex', gap: '4px', marginBottom: '6px' }}>
                            {[1, 2, 3, 4].map(i => (
                                <div key={i} style={{
                                    flex: 1, height: '4px', borderRadius: '99px',
                                    background: i <= strength ? strengthColor[strength] : 'var(--border-heavy)',
                                    transition: 'background 0.3s'
                                }} />
                            ))}
                        </div>
                        <span style={{ fontSize: '0.75rem', color: strengthColor[strength] }}>
                            {strengthLabel[strength]}
                        </span>
                    </div>
                )}

                <PasswordInput
                    label="Confirm New Password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    show={showConfirm}
                    onToggle={() => setShowConfirm(p => !p)}
                    placeholder="Re-enter new password"
                />

                <Alert type={status.type} message={status.message} />

                <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                    style={{ marginTop: '20px', padding: '10px 24px' }}
                >
                    {loading ? 'Updating...' : 'Update Password'}
                </button>
            </form>
        </div>
    );
}

// ─── Section B: Create Admin User ───────────────────────────────────────────────
function CreateAdminSection() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPwd, setShowPwd] = useState(false);
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState({ type: null, message: null });

    const handleCreateAdmin = async (e) => {
        e.preventDefault();
        setStatus({ type: null, message: null });

        if (password !== confirmPassword) {
            setStatus({ type: 'error', message: 'Passwords do not match.' });
            return;
        }
        if (password.length < 8) {
            setStatus({ type: 'error', message: 'Password must be at least 8 characters.' });
            return;
        }

        setLoading(true);
        try {
            // Uses Supabase admin signup – new user will receive a confirmation email.
            // In production you should use the service_role key via a backend function
            // (or Supabase Edge Function) to create users without requiring confirmation.
            const { data, error } = await supabase.auth.signUp({
                email,
                password,
                options: {
                    data: { role: 'admin' }
                }
            });

            if (error) throw error;

            const isImmediate = data?.user?.identities?.length > 0;
            setStatus({
                type: 'success',
                message: isImmediate
                    ? `Admin account created for ${email}. They will receive a confirmation email.`
                    : `An account with ${email} may already exist.`,
            });
            setEmail('');
            setPassword('');
            setConfirmPassword('');
        } catch (err) {
            setStatus({ type: 'error', message: err.message });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                <div style={{ background: 'rgba(16,185,129,0.1)', padding: '10px', borderRadius: '10px' }}>
                    <UserPlus size={20} color="var(--accent-success)" />
                </div>
                <div>
                    <h2 style={{ fontSize: '1.125rem', fontWeight: 600, margin: 0 }}>Create Admin Account</h2>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: '4px 0 0' }}>Invite a new administrator to the portal</p>
                </div>
            </div>

            {/* Security notice */}
            <div style={{
                background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)',
                borderRadius: '8px', padding: '12px 16px', marginBottom: '24px',
                display: 'flex', gap: '10px', color: 'var(--accent-warning)', fontSize: '0.8rem'
            }}>
                <ShieldCheck size={16} style={{ flexShrink: 0, marginTop: '1px' }} />
                <span>New admins will receive a confirmation email before they can log in. Only share credentials with trusted personnel.</span>
            </div>

            <form onSubmit={handleCreateAdmin} style={{ maxWidth: '480px' }}>
                <div className="form-group">
                    <label className="form-label">Admin Email Address</label>
                    <input
                        type="email"
                        className="form-input"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        placeholder="newadmin@yourcompany.com"
                        required
                    />
                </div>

                <div className="form-group">
                    <label className="form-label">Temporary Password</label>
                    <div style={{ position: 'relative' }}>
                        <input
                            type={showPwd ? 'text' : 'password'}
                            className="form-input"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            placeholder="Min. 8 characters"
                            required
                            style={{ paddingRight: '44px' }}
                        />
                        <button
                            type="button"
                            onClick={() => setShowPwd(p => !p)}
                            style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center' }}
                        >
                            {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                    </div>
                </div>

                <div className="form-group">
                    <label className="form-label">Confirm Password</label>
                    <input
                        type="password"
                        className="form-input"
                        value={confirmPassword}
                        onChange={e => setConfirmPassword(e.target.value)}
                        placeholder="Re-enter password"
                        required
                    />
                </div>

                <Alert type={status.type} message={status.message} />

                <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                    style={{ marginTop: '20px', padding: '10px 24px', background: 'var(--accent-success)' }}
                >
                    {loading ? 'Creating Account...' : 'Create Admin Account'}
                </button>
            </form>
        </div>
    );
}

// ─── Main export ─────────────────────────────────────────────────────────────────
export default function AdminSettings() {
    return (
        <div>
            <div style={{ marginBottom: '32px' }}>
                <h1 style={{ fontSize: '1.75rem', fontFamily: 'var(--font-display)', display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <ShieldCheck color="var(--accent-primary)" /> Admin Settings
                </h1>
                <p style={{ color: 'var(--text-secondary)' }}>
                    Manage admin credentials and create new administrator accounts.
                </p>
            </div>

            <ChangePasswordSection />
            <CreateAdminSection />
        </div>
    );
}
